/**
 * PlotTrack generate copy (#827). Part 4: one standing line, regenerate
 * names what it replaces. Writer-facing word is session, not sitting.
 * Chat is the room noun. #946 wait progress. #948 replace wording.
 */

import { formatApiErrorDetail } from './apiErrorDetail';
import {
  WHOLE_OUTLINE_SCOPE,
  normalizeOfferScope,
  offerScopeLabel,
} from './plottrackOfferPersist';
import { allScenesAccepted } from './bookPathTip';
import {
  PLOTTRACK_EMPTY_GENERATE,
  PLOTTRACK_FIGHT,
  PLOTTRACK_FIGHT_EDIT,
  PLOTTRACK_FILL_EMPTY_HINT,
  PLOTTRACK_KEEP_NOTE,
  PLOTTRACK_KEEP_TALK,
  PLOTTRACK_MODE_FILLS,
  PLOTTRACK_MODE_FILLS_ONLY,
  PLOTTRACK_MODE_STEER,
  PLOTTRACK_MODE_TALK_DOCS,
  PLOTTRACK_OUTLINE_LOADING,
  PLOTTRACK_NO_FRAMEWORK_STEER,
  PLOTTRACK_NOTES_HANDY,
  PLOTTRACK_OFFER_ACCEPT,
  PLOTTRACK_OFFER_EDIT,
  PLOTTRACK_OFFER_REJECT,
  PLOTTRACK_READING,
  PLOTTRACK_REPLACE_EXISTING,
  PLOTTRACK_REPLACE_STAYS,
  PLOTTRACK_STANDING_DISCLAIMER,
  PLOTTRACK_THIN_WARN,
  PLOTTRACK_THIN_WORKSHOP,
  OUTLINE_EXISTS_LEAD,
  OUTLINE_EXISTS_NEXT_STEP,
  PATH_ALL_DRAFTED,
  PLOT_WORKSHOP_GENERATE_BLOCKED,
  PLOT_WORKSHOP_SEE_GAPS,
  PLOTTRACK_USE_NOTES,
  PLOTTRACK_ENTITY_BLEED,
  PLOTTRACK_INCOMPLETE_OUTLINE,
  PLOTTRACK_CONTINUE_INCOMPLETE,
  PLOTTRACK_ACCEPT_ANYWAY,
  PLOTTRACK_FINISHED_INCOMPLETE,
} from './writerCopy';

export const STANDING_DISCLAIMER = PLOTTRACK_STANDING_DISCLAIMER;

// Trial / not a lock (#866 / #868). Show when series talks were background.
export const SERIES_BACKGROUND_TRIAL_COPY = (
  "Trial: this book's outline was also inferred from series chats. "
  + "Not a lock. The grid is still this book's."
);

export function generateDisclaimer({
  disclaimer,
  series_background,
  generated,
} = {}) {
  if (generated === false && !disclaimer && !series_background) {
    return null;
  }
  let text = String(disclaimer || STANDING_DISCLAIMER).trim();
  if (series_background && !/trial:/i.test(text)) {
    text = `${text} ${SERIES_BACKGROUND_TRIAL_COPY}`;
  }
  return text || null;
}

// Ordinary words when Generate writes nothing. Not the standing inferred-from
// sessions caption. People / world / series Discovery do not fill this book.
export const EMPTY_GENERATE_COPY = PLOTTRACK_EMPTY_GENERATE;

function joinNamed(kind, names) {
  const shown = names.filter(Boolean).slice(0, 8);
  const extra = names.length - shown.length;
  if (shown.length === 1) return `the ${kind} ${shown[0]}`;
  let text = `${names.length} ${kind}s (${shown.join(', ')}`;
  if (extra > 0) text += `, +${extra} more`;
  return `${text})`;
}

export function replaceWarning({ rows = [], columns = [], headers = {} } = {}) {
  const rowNames = rows.map((row) => row.label || row.name || row.type || 'thread');
  const sceneNames = columns.map((col) => col.display_name || col.name || 'scene');
  const acts = (headers.acts || []).map((h) => h.display_name).filter(Boolean);
  const sequences = (headers.sequences || []).map((h) => h.display_name).filter(Boolean);
  const parts = [];
  if (rowNames.length) parts.push(joinNamed('thread row', rowNames));
  if (sceneNames.length) parts.push(joinNamed('scene', sceneNames));
  if (acts.length) parts.push(joinNamed('act header', acts));
  if (sequences.length) parts.push(joinNamed('sequence header', sequences));
  if (!parts.length) {
    return (
      `This replaces the current Plot Grid grid. ${PLOTTRACK_REPLACE_STAYS}`
    );
  }
  return (
    `This replaces ${parts.join(', then ')}. ${PLOTTRACK_REPLACE_STAYS}`
  );
}

export function generateErrorCopy(err) {
  const raw = formatApiErrorDetail(err?.detail)
    || formatApiErrorDetail(err?.message)
    || formatApiErrorDetail(err);
  if (!raw || /internal error/i.test(raw) || /\[object Object\]/i.test(raw)) {
    return 'Plot Grid could not generate from those sessions. Try again.';
  }
  return raw.replace(/^VALIDATION:\s*[^-]+-\s*/, '') || raw;
}

// #948 — Rebuild was ambiguous. Name the replace on the control.
export const REPLACE_OUTLINE_LABEL = 'Replace this outline';
export const REPLACE_OUTLINE_HINT = 'This replaces the current outline.';
export const REPLACE_CONFIRM_TITLE = 'Replace this outline?';
/** Accepted prose would be deleted: the writer types this to confirm. */
export const REPLACE_TYPE_WORD = 'replace';
export const REPLACE_TYPE_LABEL = 'Type replace to confirm';
/** Toolbar Replace now shows the new outline before anything is replaced. */
export const REPLACE_REVIEW_FIRST_NOTE = (
  'Review the new outline below. If you want it instead of yours, choose Replace my whole outline.'
);

/** Same words as the server's prose_loss_copy. */
export function proseLossLine({ drafted = 0, accepted = 0 } = {}) {
  if (!drafted) return '';
  const scenes = drafted === 1 ? '1 scene' : `${drafted} scenes`;
  const kept = accepted ? `, including ${accepted} you accepted` : '';
  return `This also takes the drafted prose off ${scenes}${kept}. It moves to Drafts not in your outline, in Scene Writing.`;
}

/** The 409 the server sends when a write would delete drafted prose. */
export function proseLossFromError(err) {
  const detail = err?.detail;
  if (err?.status !== 409 || !detail || detail.code !== 'confirm_prose_loss') return null;
  return {
    drafted: Number(detail.drafted_scenes) || 0,
    accepted: Number(detail.accepted_scenes) || 0,
  };
}

// #946 — Generate is one wait, often ~30s, with no server ticks.
// #1222 — that timer is not a server clock. Stop the wait at this bound.
export const GENERATE_TYPICAL_MS = 30000;
export const GENERATE_TIMEOUT_MS = 180000;

export const GENERATE_TIMED_OUT_COPY = (
  'This took too long and stopped. The grid was not changed. Try again.'
);
export const GENERATE_CANCELLED_COPY = 'Stopped. The grid was not changed.';
export const GENERATE_CANCEL_LABEL = 'Cancel';
export const GENERATE_RETRY_LABEL = 'Try again';

export const GENERATE_PROGRESS_STEPS = [
  { afterMs: 0, text: PLOTTRACK_READING },
  { afterMs: 8000, text: 'Building the outline from those sessions…' },
  { afterMs: 18000, text: 'Placing the rows…' },
  { afterMs: 28000, text: 'Still working. This can take a little longer…' },
];

export const GENERATE_FINISHED_GRID = 'Done. The outline is below.';
export const GENERATE_EMPTY_RESULT_COPY = (
  'Plot Grid couldn\'t generate from those sessions. Try again.'
);
export const GENERATE_FINISHED_INCOMPLETE = PLOTTRACK_FINISHED_INCOMPLETE;
export const ENTITY_BLEED_FLAG_COPY = PLOTTRACK_ENTITY_BLEED;
export const INCOMPLETE_OUTLINE_COPY = PLOTTRACK_INCOMPLETE_OUTLINE;
export const CONTINUE_INCOMPLETE_LABEL = PLOTTRACK_CONTINUE_INCOMPLETE;
export const ACCEPT_ANYWAY_LABEL = PLOTTRACK_ACCEPT_ANYWAY;

export function generateProgressCopy(elapsedMs) {
  const elapsed = Number(elapsedMs) || 0;
  let text = GENERATE_PROGRESS_STEPS[0].text;
  for (const step of GENERATE_PROGRESS_STEPS) {
    if (elapsed >= step.afterMs) text = step.text;
  }
  return text;
}

export function generateEtaCopy(elapsedMs, typicalMs = GENERATE_TYPICAL_MS) {
  const elapsed = Number(elapsedMs) || 0;
  const remainingSec = Math.ceil((typicalMs - elapsed) / 1000);
  if (remainingSec <= 0) {
    const leftMs = GENERATE_TIMEOUT_MS - elapsed;
    if (leftMs <= 60000) {
      return 'Still working. This stops within a minute if it is not done.';
    }
    const leftMin = Math.ceil(leftMs / 60000);
    return `Still working. This stops in about ${leftMin} minutes if it is not done.`;
  }
  if (remainingSec === 1) return 'About 1 second left.';
  return `About ${remainingSec} seconds left.`;
}

/**
 * One in-flight Generate. The timer ends the wait even if the request
 * never settles. Cancel ends it sooner. A late result is dropped.
 */
export function createGenerateWait(timeoutMs = GENERATE_TIMEOUT_MS) {
  const controller = new AbortController();
  let reason = null;
  let rejectGate = null;
  const gate = new Promise((_, reject) => {
    rejectGate = reject;
  });
  const stop = (nextReason, name) => {
    if (reason) return;
    reason = nextReason;
    controller.abort();
    const err = new Error(nextReason);
    err.name = name;
    rejectGate(err);
  };
  const timer = setTimeout(() => {
    stop('timeout', 'GenerateTimeoutError');
  }, timeoutMs);
  return {
    signal: controller.signal,
    cancel() {
      clearTimeout(timer);
      stop('cancel', 'GenerateCancelError');
    },
    clear() {
      clearTimeout(timer);
    },
    race(promise) {
      const guarded = Promise.resolve(promise).then((value) => {
        if (reason) return undefined;
        return value;
      }, (err) => {
        if (reason === 'timeout') {
          const wrapped = new Error('timeout');
          wrapped.name = 'GenerateTimeoutError';
          throw wrapped;
        }
        if (reason === 'cancel' || err?.name === 'AbortError') {
          const wrapped = new Error('cancel');
          wrapped.name = 'GenerateCancelError';
          throw wrapped;
        }
        throw err;
      });
      return Promise.race([guarded, gate]).finally(() => {
        clearTimeout(timer);
      });
    },
  };
}

export function generateProgressPercent(elapsedMs, typicalMs = GENERATE_TYPICAL_MS) {
  const elapsed = Math.max(0, Number(elapsedMs) || 0);
  const typical = typicalMs > 0 ? typicalMs : GENERATE_TYPICAL_MS;
  const raw = (elapsed / typical) * 100;
  return Math.min(95, Math.max(4, Math.round(raw)));
}

export function generateFinishedCopy({ hasGrid, complete = true } = {}) {
  if (complete === false) {
    return assertNoBannedGenerateCopy(GENERATE_FINISHED_INCOMPLETE);
  }
  if (!hasGrid) return '';
  return GENERATE_FINISHED_GRID;
}

/** What to show after Generate returns. An empty grid is not "Done."
 * Scene columns count. A thread row with no scenes does not. */
export function finishGenerateNote(next) {
  const hasGrid = Array.isArray(next?.columns) && next.columns.length > 0;
  const missing = (next?.missing_sequences || []).length > 0;
  const complete = next?.complete !== false && !missing;
  if (!hasGrid && complete && (next?.wrote === true || next?.generated === true)) {
    return { note: null, error: GENERATE_EMPTY_RESULT_COPY };
  }
  const note = generateFinishedCopy({ hasGrid, complete });
  return { note: note || null, error: null };
}

function asText(value) {
  if (value == null || typeof value === 'boolean') return '';
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number') return Number.isFinite(value) ? String(value) : '';
  if (Array.isArray(value)) {
    return value.map(asText).filter(Boolean).join(' ').trim();
  }
  if (typeof value === 'object') {
    const nested = value.text ?? value.content ?? value.echo ?? value.phrase
      ?? value.name ?? value.title ?? value.label ?? value.display_name
      ?? value.act_name;
    if (nested != null && nested !== value) return asText(nested);
    return '';
  }
  return String(value).trim();
}

function nameList(value) {
  const raw = Array.isArray(value) ? value : (value == null || value === '' ? [] : [value]);
  return raw.map(asText).filter(Boolean);
}

const BANNED_COPY = /\b(hang|hung|fence|ledger|cards)\b/i;

// Story names can contain ordinary words our chrome must not use.
// Drop the line. Do not throw — a throw here unmounts Plot Grid.
function safeWriterLine(text) {
  const line = asText(text);
  if (!line || BANNED_COPY.test(line)) return null;
  return line;
}

export function offerFieldText(value, fallback = '') {
  return asText(value) || fallback;
}

export function offerIntegrityNotes(offer) {
  // Main status copy lives on offer.disclaimer. Extra lines list unsupported names
  // and a Continue hint when the disclaimer is absent.
  // Never throw: Resume renders this during React render (#1149).
  const notes = [];
  const grounding = offer?.entity_grounding || {};
  const unsupported = nameList(grounding.unsupported_names)
    .filter((name) => !BANNED_COPY.test(name))
    .slice(0, 8);
  if (grounding.dominated && unsupported.length) {
    const line = safeWriterLine(
      `Unsupported names in this offer: ${unsupported.join(', ')}.`,
    );
    if (line) notes.push(line);
  }
  if (!asText(offer?.disclaimer)) {
    if (grounding.dominated) {
      const flag = safeWriterLine(grounding.flag_copy || ENTITY_BLEED_FLAG_COPY);
      if (flag) notes.push(flag);
    }
    if (offer?.complete === false) {
      const cont = safeWriterLine(offer?.continue_copy || INCOMPLETE_OUTLINE_COPY);
      if (cont) notes.push(cont);
    }
  }
  return notes;
}

export const USE_NOTES_LABEL = PLOTTRACK_USE_NOTES;
export const MODE_LINE_TALK_DOCS = PLOTTRACK_MODE_TALK_DOCS;
export const MODE_LINE_STEER = PLOTTRACK_MODE_STEER;
export const MODE_LINE_FILLS = PLOTTRACK_MODE_FILLS;
export const MODE_LINE_FILLS_ONLY = PLOTTRACK_MODE_FILLS_ONLY;
export const OUTLINE_LOADING_COPY = PLOTTRACK_OUTLINE_LOADING;
export const THIN_WARN_COPY = PLOTTRACK_THIN_WARN;
export const THIN_WARN_WORKSHOP = PLOTTRACK_THIN_WORKSHOP;
export const WORKSHOP_GENERATE_BLOCKED = PLOT_WORKSHOP_GENERATE_BLOCKED;
export { OUTLINE_EXISTS_NEXT_STEP };
export const WORKSHOP_SEE_GAPS = PLOT_WORKSHOP_SEE_GAPS;
export const NOTES_HANDY_COPY = PLOTTRACK_NOTES_HANDY;
export const NO_FRAMEWORK_STEER_COPY = PLOTTRACK_NO_FRAMEWORK_STEER;
export const OFFER_ACCEPT_LABEL = PLOTTRACK_OFFER_ACCEPT;
export const ACCEPT_FILL_EMPTY_LABEL = 'Add to my outline';
/** #1351: shown on offer scenes for a sequence the writer already approved. */
export const OFFER_KEPT_NOTE = 'Already approved — stays as is, not added';

/** Run 11: why Add to my outline leaves an offered scene out, keyed by sequence + name. */
export function offerSkipKey(sequence, name) {
  return `${String(sequence || '').trim()}\u0000${String(name || '').trim()}`;
}

export function offerSkippedReasons(offer) {
  const reasons = new Map();
  const rows = Array.isArray(offer?.fill_preview?.skipped) ? offer.fill_preview.skipped : [];
  rows.forEach((row) => {
    if (!row || typeof row !== 'object' || row.kind === 'approved_sequence') return;
    const reason = String(row.reason || '').trim();
    if (reason) reasons.set(offerSkipKey(row.sequence, row.name), reason);
  });
  return reasons;
}

/**
 * Run 11: after Add to my outline, every offered scene it left out, and why.
 * Book 1's "Alone in the car…" was left out with nothing on screen saying so.
 */
export function fillSkippedLines(skipped) {
  const rows = Array.isArray(skipped) ? skipped.filter((row) => row && typeof row === 'object') : [];
  const lines = [];
  const approved = new Map();
  rows.forEach((row) => {
    const name = String(row.name || 'Scene').trim();
    if (row.kind === 'approved_sequence') {
      const sequence = String(row.sequence || 'A sequence').trim();
      if (!approved.has(sequence)) approved.set(sequence, []);
      approved.get(sequence).push(name);
      return;
    }
    const reason = String(row.reason || '').trim();
    lines.push(reason ? `\u201c${name}\u201d: ${reason}` : `\u201c${name}\u201d`);
  });
  approved.forEach((names, sequence) => {
    const quoted = names.map((name) => `\u201c${name}\u201d`).join(', ');
    lines.push(`${quoted}: ${sequence} already has your approved scenes, so it stays as is.`);
  });
  return lines;
}

export const FILL_SKIPPED_HEADING = 'Not added:';

/** Run 23: Framework sequences still without a scene after Generate, named so the
 * writer can fill them (Generate had called the outline complete). */
export function missingSequencesLine(missing) {
  const names = (Array.isArray(missing) ? missing : [])
    .map((name) => String(name || '').trim())
    .filter(Boolean);
  if (names.length === 0) return '';
  const quoted = names.map((name) => `\u201c${name}\u201d`).join(', ');
  const these = names.length === 1 ? 'This sequence has' : 'These sequences have';
  return `${these} no scenes yet: ${quoted}. Fill ${names.length === 1 ? 'it' : 'them one at a time'} in Plot Workshop.`;
}
export const OFFER_EDIT_LABEL = PLOTTRACK_OFFER_EDIT;
export const OFFER_REJECT_LABEL = PLOTTRACK_OFFER_REJECT;
export const FILL_EMPTY_HINT = PLOTTRACK_FILL_EMPTY_HINT;
export const REPLACE_EXISTING_LABEL = PLOTTRACK_REPLACE_EXISTING;
export const PENDING_OFFER_NOTE = 'A generated outline is waiting. Resume to review it, or discard it.';
export const PENDING_OFFER_RESUME = 'Resume';
export const PENDING_OFFER_DISCARD = 'Discard';
export const PENDING_OFFER_REPLACE_TITLE = 'Replace this draft?';
export const PENDING_OFFER_REPLACE_CONFIRM = 'Generate again';
export const PENDING_OFFER_WHOLE_LABEL = 'Whole outline';

/** Same-scope regenerate only. Another sequence does not use this line. */
export function pendingOfferReplaceCopy(scope) {
  const name = String(scope || '').trim();
  if (!name || name === WHOLE_OUTLINE_SCOPE || name === PENDING_OFFER_WHOLE_LABEL) {
    return 'A generated outline is waiting. Generating again replaces that draft.';
  }
  return `A generated outline is waiting for ${name}. Generating again replaces that draft.`;
}

export const PENDING_OFFER_SHOWING_SUFFIX = '(showing below)';

/** One row in the waiting list. The draft on screen keeps the same Resume and Discard. */
export function pendingOfferListLabel(scope, { showing = false } = {}) {
  const name = offerScopeLabel(scope);
  const base = `${name} — waiting`;
  return showing ? `${base} ${PENDING_OFFER_SHOWING_SUFFIX}` : base;
}

/**
 * Same scope with a waiting draft must warn before the cost dialog.
 * A different scope does not. `liveScope` is the draft on screen when storage
 * missed the write; pass null when nothing is on screen.
 */
export function pendingOfferReplaceNeeded(pendingByScope, scope, liveScope = undefined) {
  const key = normalizeOfferScope(scope);
  const map = pendingByScope && typeof pendingByScope === 'object' && !Array.isArray(pendingByScope)
    ? pendingByScope
    : null;
  if (map && map[key]) return true;
  if (liveScope == null || String(liveScope).trim() === '') return false;
  return normalizeOfferScope(liveScope) === key;
}
export const FIGHT_COPY = PLOTTRACK_FIGHT;
export const FIGHT_KEEP_NOTE = PLOTTRACK_KEEP_NOTE;
export const FIGHT_KEEP_TALK = PLOTTRACK_KEEP_TALK;
export const FIGHT_EDIT_LABEL = PLOTTRACK_FIGHT_EDIT;
export { CONTINUE_INCOMPLETE_LABEL as CONTINUE_INCOMPLETE_BUTTON };

export function hasChosenFramework(frameworkRef) {
  const key = String(frameworkRef || '').trim().toLowerCase();
  return Boolean(key) && key !== 'none';
}

function writerModeLine(modeLine) {
  const line = String(modeLine || '').trim();
  if (!line || /\btalk\b/i.test(line)) return '';
  return line;
}

export function generateModeLine({
  useNotes, hasFramework, fillsSpine, modeLine, hasTalk,
} = {}) {
  const server = writerModeLine(modeLine);
  if (fillsSpine) return server || (hasTalk === false ? MODE_LINE_FILLS_ONLY : MODE_LINE_FILLS);
  if (useNotes && hasFramework) return MODE_LINE_STEER;
  if (hasFramework) {
    if (server) return server;
    return hasTalk === false ? MODE_LINE_FILLS_ONLY : MODE_LINE_FILLS;
  }
  return server || MODE_LINE_TALK_DOCS;
}

// The Use Notes toggle shows only with a Framework (runs 33-34), so a
// stored "on" from an earlier Framework says nothing without one.
export function generateFallbackCopy() {
  return null;
}

export function thinWarnCopy({ thin, hole } = {}) {
  if (!thin) return null;
  const needsWorkshop = Boolean(hole?.framework || hole?.workshop);
  return needsWorkshop ? `${THIN_WARN_COPY}${THIN_WARN_WORKSHOP}` : THIN_WARN_COPY;
}

/** Spend stays closed unless the Workshop check said Ready. Pending counts as closed. */
export function workshopSpendOpen(readiness, { pending = false } = {}) {
  if (pending || !readiness) return false;
  if (readiness.workshop_ready !== true) return false;
  if (readiness.enough_solid === false) return false;
  if (readiness.ready_to_generate === false) return false;
  return true;
}

/** Grid columns, the readiness payload, or the book's scene count. */
export function outlineAlreadyExists(readiness, hasOutline = false, bookPath = null) {
  // Runs 27-30: an outline that is only a start (some Framework sequences have no
  // scene, none accepted) keeps Generate open to fill the rest. The server decides.
  if (readiness?.generate_open_with_outline === true) return false;
  return Boolean(hasOutline)
    || readiness?.has_outline === true
    || (bookPath?.scenes_total || 0) > 0;
}

/**
 * True until the grid check and the book path have both arrived, and neither
 * has said an outline exists. Generate stays closed for that whole window.
 */
/**
 * Run 46: on reload the board's readiness has no answer on a started outline
 * yet, so the grid's scenes read as "already has an outline" until the check
 * says Generate can still fill the rest. Wait for the check instead.
 */
export function outlineVerdictPending(readiness, assessPending = false) {
  return Boolean(assessPending) && typeof readiness?.generate_open_with_outline !== 'boolean';
}

export function outlineStillLoading({
  hasOutline = false,
  readiness = null,
  bookPath = null,
  bookPathStatus = 'idle',
  assessPending = false,
} = {}) {
  if (outlineVerdictPending(readiness, assessPending)) return true;
  if (outlineAlreadyExists(readiness, hasOutline, bookPath)) return false;
  return bookPathStatus === 'loading' || assessPending;
}

/**
 * Once an outline exists. While this visit's path is still loading, the
 * next-step sentence stays off. When every scene is accepted, the existing
 * all-accepted line replaces that next step.
 */
export function outlineExistsCopy(bookPath, bookPathStatus = 'idle') {
  if (allScenesAccepted(bookPath)) {
    return `${OUTLINE_EXISTS_LEAD} ${PATH_ALL_DRAFTED}`;
  }
  if (bookPathStatus === 'loading') return OUTLINE_EXISTS_LEAD;
  return OUTLINE_EXISTS_NEXT_STEP;
}

export function workshopGenerateClosedCopy(readiness, {
  pending = false,
  hasOutline = false,
  bookPath = null,
  bookPathStatus = 'idle',
} = {}) {
  // Show the next step even when the Workshop check said Ready. That is the
  // case QA hit: Plot Grid left Generate open and never painted this line.
  if (outlineVerdictPending(readiness, pending)) return OUTLINE_LOADING_COPY;
  if (outlineAlreadyExists(readiness, hasOutline, bookPath)) {
    return outlineExistsCopy(bookPath, bookPathStatus);
  }
  if (outlineStillLoading({
    hasOutline,
    readiness,
    bookPath,
    bookPathStatus,
    assessPending: pending,
  })) return OUTLINE_LOADING_COPY;
  if (workshopSpendOpen(readiness, { pending })) return '';
  const namedReady = readiness?.workshop_ready === true;
  const known = String(readiness?.workshop_block_copy || '').trim();
  if (known && !namedReady) return known;
  const names = (Array.isArray(readiness?.workshop_gaps) ? readiness.workshop_gaps : [])
    .map((name) => String(name || '').trim())
    .filter(Boolean);
  if (names.length && !namedReady) {
    return `Still to do: ${names.join(', ')}. ${WORKSHOP_GENERATE_BLOCKED}`;
  }
  return `${WORKSHOP_GENERATE_BLOCKED} ${WORKSHOP_SEE_GAPS}`;
}

export function assertNoBannedGenerateCopy(text) {
  if (BANNED_COPY.test(String(text || ''))) {
    throw new Error('Writer-facing copy cannot use banned words');
  }
  return text;
}

function actList(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * #1343: one plain line on what the offer adds and what stays. Counts come
 * from the offer itself; "stays as it is" is said only when the book already
 * has an outline (Add to my outline leaves approved sequences alone, #1332).
 */
/** A scene Generate titled but filled with nothing (no beats, no note). */
export function isPlaceholderScene(scene) {
  if (!scene || typeof scene !== 'object' || scene.grain === 'sequence') return false;
  const beats = offerSceneBeatLines(scene);
  return beats.length === 0 && !String(scene.note || '').trim();
}

export function offerPlaceholderCount(offer) {
  const kept = new Set(offer?.fill_preview?.kept_offer_sequences || []);
  return offerScenes(offer).filter((row) => !kept.has(row.sequence) && isPlaceholderScene(row)).length;
}

export function offerSummary(offer, { hasOutline = false } = {}) {
  // #1351: when the book has an outline, say exactly what will be added.
  const preview = offer?.fill_preview;
  if (hasOutline && preview && typeof preview.scenes === 'number') {
    if (preview.scenes === 0) {
      return 'Nothing new to add. Every sequence in this outline already has your scenes.';
    }
    const sceneWord = preview.scenes === 1 ? 'new scene' : 'new scenes';
    const parts = [
      preview.sequences > 1
        ? `${preview.scenes} ${sceneWord} across ${preview.sequences} sequences.`
        : `${preview.scenes} ${sceneWord}.`,
    ];
    const kept = Array.isArray(preview.kept) ? preview.kept.filter(Boolean) : [];
    if (kept.length === 1) parts.push(`${kept[0]} stays as you approved it.`);
    if (kept.length > 1) parts.push(`${kept.length} sequences you approved stay as they are.`);
    // Run 11: offered scenes that repeat one already on your outline.
    const repeats = Array.isArray(preview.skipped)
      ? preview.skipped.filter((row) => row && row.kind !== 'approved_sequence').length
      : 0;
    if (repeats === 1) parts.push('1 offered scene repeats one you already have, so it is not added.');
    if (repeats > 1) parts.push(`${repeats} offered scenes repeat ones you already have, so they are not added.`);
    if (preview.last_sequence) parts.push(`Ends at: ${preview.last_sequence}.`);
    return parts.join(' ');
  }
  const rows = offerScenes(offer);
  if (!rows.length) return '';
  // Count sequences on the plan, including one the reply left empty.
  // Scene rows alone hide that sequence and the line says 15 for a 16-sequence Framework.
  const sequences = [];
  for (const act of actList(offer?.plan?.acts)) {
    for (const seq of actList(act?.sequences)) {
      const name = String(seq?.name || seq?.title || '').trim();
      if (name && !sequences.includes(name)) sequences.push(name);
    }
  }
  if (!sequences.length) {
    rows.forEach((row) => {
      const name = String(row.sequence || '').trim();
      if (name && !sequences.includes(name)) sequences.push(name);
    });
  }
  const sceneWord = rows.length === 1 ? 'scene' : 'scenes';
  const parts = [
    sequences.length > 1
      ? `${rows.length} ${sceneWord} across ${sequences.length} sequences.`
      : `${rows.length} ${sceneWord}.`,
  ];
  if (hasOutline) parts.push('Sequences you already approved stay as they are.');
  const last = sequences[sequences.length - 1];
  if (last) parts.push(`Ends at: ${last}.`);
  return parts.join(' ');
}

export function offerScenes(offer) {
  const stored = actList(offer?.scenes).filter((scene) => scene && typeof scene === 'object');
  if (stored.length) return stored;
  const scenes = [];
  for (const act of actList(offer?.plan?.acts)) {
    if (!act || typeof act !== 'object') continue;
    for (const seq of actList(act.sequences)) {
      if (!seq || typeof seq !== 'object') continue;
      for (const scene of actList(seq.scenes)) {
        if (!scene || typeof scene !== 'object') continue;
        const row = {
          act: act.name || act.title || 'Act',
          sequence: seq.name || seq.title || 'Sequence',
          name: scene.name || scene.title || 'Scene',
          beats: scene.beats || [],
          note: scene.note || '',
        };
        if (scene.grain === 'sequence' || scene.grain === 'scene') {
          row.grain = scene.grain;
        }
        if (scene.from_stub) row.from_stub = true;
        scenes.push(row);
      }
    }
  }
  return scenes;
}

export function offerGrainLabel(scene) {
  return scene?.grain === 'sequence' ? 'Sequence' : 'Scene';
}

/**
 * The Framework sequence a scene row sits in, or '' for the placeholder
 * offerScenes uses when the plan gave none (run 46: name sequences in the offer).
 */
export function offerSequenceName(scene) {
  if (!scene || typeof scene !== 'object' || scene.grain === 'sequence') return '';
  const name = offerFieldText(scene.sequence, '').trim();
  return name && name !== 'Sequence' ? name : '';
}

/** Scenes per named sequence, in offer order. */
export function offerSequenceCounts(scenes) {
  const counts = new Map();
  for (const scene of Array.isArray(scenes) ? scenes : []) {
    const name = offerSequenceName(scene);
    if (name) counts.set(name, (counts.get(name) || 0) + 1);
  }
  return counts;
}

export function offerSequenceLabel(name, count) {
  if (!count) return name;
  return `${name} · ${count} ${count === 1 ? 'scene' : 'scenes'}`;
}

/** Read-view line. A sequence-sized cell is not labeled as only a scene. */
export function offerSceneHeading(scene) {
  const act = offerFieldText(scene?.act, 'Act');
  const grain = offerGrainLabel(scene);
  const name = offerFieldText(scene?.name, grain);
  if (scene?.grain === 'sequence') {
    return `${act} — Sequence — ${name}`;
  }
  return `${act} — ${name}`;
}

/**
 * Framework slot for a confirmed stub scene.
 * Empty when this row is not one child of a confirmed list.
 */
export function offerSequenceSlot(scene) {
  if (!scene || typeof scene !== 'object' || scene.grain === 'sequence') return null;
  if (!scene.from_stub) return null;
  const sequence = offerFieldText(scene.sequence, '').trim();
  if (!sequence) return null;
  const act = offerFieldText(scene.act, '').trim();
  return { act, sequence };
}

/**
 * Child line under a confirmed sequence. The stub text, not a clipped title,
 * unless the writer has edited the name.
 */
export function offerStubChildLabel(scene) {
  const name = offerFieldText(scene?.name, '');
  const beats = offerSceneBeatLines(scene);
  const stub = beats[0] || '';
  if (!stub) return name || offerGrainLabel(scene);
  if (!name || name === stub || stub.startsWith(name)) return stub;
  return name;
}

/** Beat lines that are not already the stub child label. */
export function offerStubExtraBeats(scene) {
  const label = offerStubChildLabel(scene);
  return offerSceneBeatLines(scene).filter((line) => line !== label);
}

/**
 * One Framework sequence header, then each confirmed stub as a child.
 * The header is the slot name. Stub titles are not part of the header.
 * Rows that are not confirmed stubs stay one line each.
 */
export function groupOfferScenes(scenes) {
  const groups = [];
  const list = Array.isArray(scenes) ? scenes : [];
  list.forEach((scene, index) => {
    const slot = offerSequenceSlot(scene);
    if (!slot) {
      groups.push({ kind: 'scene', scene, index });
      return;
    }
    const existing = groups.find((group) => (
      group.kind === 'sequence'
      && group.act === slot.act
      && group.sequence === slot.sequence
    ));
    if (existing) {
      existing.children.push({ scene, index });
      return;
    }
    groups.push({
      kind: 'sequence',
      act: slot.act,
      sequence: slot.sequence,
      heading: slot.sequence,
      children: [{ scene, index }],
    });
  });
  return groups;
}

export function offerSceneBeatLines(scene) {
  const raw = scene?.beats;
  // A fill phrase sometimes arrives as one string. Do not walk its letters.
  let list;
  if (Array.isArray(raw)) list = raw;
  else if (raw == null || raw === '') list = [];
  else list = [raw];
  const lines = [];
  for (const beat of list) {
    const text = typeof beat === 'string' ? beat : asText(beat);
    if (typeof text !== 'string') continue;
    if (text === ' ') {
      lines.push(' ');
      continue;
    }
    const trimmed = text.trim();
    if (trimmed) lines.push(trimmed);
  }
  // Older generates stored a string beat as one character per line.
  // Painting those blanks the page. Join them back into one line.
  const single = lines.filter((line) => line.length === 1).length;
  if (lines.length >= 12 && single / lines.length >= 0.8) {
    const joined = lines.join('').replace(/\s+/g, ' ').trim();
    return joined ? [joined] : [];
  }
  return lines.filter((line) => line.trim()).slice(0, 24);
}

export function offerFightRows(offer) {
  return actList(offer?.fights).filter((fight) => fight && typeof fight === 'object');
}

/** Scenes on the line. A thread row with no columns is not an outline. */
export function gridHasOutline(columns) {
  return Array.isArray(columns) && columns.length > 0;
}

export function acceptOfferLabel(hasOutline) {
  return hasOutline ? ACCEPT_FILL_EMPTY_LABEL : OFFER_ACCEPT_LABEL;
}

export function editOfferScene(plan, sceneIndex, patch) {
  const acts = (plan?.acts || []).map((act) => ({
    ...act,
    sequences: (act.sequences || []).map((seq) => ({
      ...seq,
      scenes: (seq.scenes || []).map((scene) => ({ ...scene })),
    })),
  }));
  let index = 0;
  for (const act of acts) {
    for (const seq of act.sequences || []) {
      for (let i = 0; i < (seq.scenes || []).length; i += 1) {
        if (index === sceneIndex) {
          seq.scenes[i] = { ...seq.scenes[i], ...patch };
          return { acts };
        }
        index += 1;
      }
    }
  }
  return { acts };
}

/** Generate is open on a started outline: say what it fills and what stays. */
export function fillsRestCopy(readiness) {
  if (readiness?.generate_open_with_outline !== true) return '';
  const n = (readiness.missing_sequences || []).length;
  if (n <= 0) return '';
  return `Generate fills the ${n} Framework sequence${n === 1 ? '' : 's'} with no scenes yet. `
    + 'Sequences you already approved stay as they are.';
}
